import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import api from '../services/api';
import { sendWS } from '../services/websocket';

function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

export default function VirtualSigns() {
  const [signs, setSigns] = useState([]);
  const [signTypes, setSignTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showList, setShowList] = useState(true);
  const [form, setForm] = useState({
    sign_type: '', kind: 'custom', value: null, label: '', lat: -4.325, lng: 15.322,
    radius_m: 500, message_fr: '', alert_distances: [1000, 500, 200],
  });

  useEffect(() => {
    loadSigns();
    loadSignTypes();
  }, []);

  const loadSigns = () => {
    api.get('/signs/').then((r) => setSigns(r.data.results || r.data)).catch(() => {});
  };

  const loadSignTypes = () => {
    api.get('/sign-types/').then((r) => setSignTypes(r.data.results || r.data)).catch(() => {});
  };

  const signTypeMap = Object.fromEntries(signTypes.map((t) => [t.id, t]));

  const handleMapClick = (latlng) => {
    setForm({ ...form, lat: latlng.lat, lng: latlng.lng });
    setShowForm(true);
  };

  const handleSignTypeSelect = (val) => {
    const typeId = parseInt(val);
    const st = typeId ? signTypeMap[typeId] : null;
    if (st) {
      setForm({ ...form, sign_type: st.id, kind: st.key, label: st.label, message_fr: st.default_message_fr || '' });
    } else {
      setForm({ ...form, sign_type: '', kind: 'custom', label: '', message_fr: '' });
    }
  };

  const handleSave = () => {
    const payload = { ...form };
    if (!payload.sign_type) delete payload.sign_type;
    // Utiliser le kind du sign_type sélectionné, ou 'custom' par défaut
    if (!payload.kind) payload.kind = 'custom';
    api.post('/signs/', payload).then(() => {
      setShowForm(false);
      loadSigns();
      sendWS('sign.create', payload);
      setForm({ sign_type: '', kind: 'custom', value: null, label: '', lat: -4.325, lng: 15.322, radius_m: 500, message_fr: '', alert_distances: [1000, 500, 200] });
    }).catch(() => {});
  };

  const handleDelete = (id) => {
    api.delete(`/signs/${id}/`).then(() => {
      loadSigns();
      sendWS('sign.delete', { sign_id: id });
    }).catch(() => {});
  };

  const handleToggle = (sign) => {
    api.patch(`/signs/${sign.id}/`, { active: !sign.active }).then(() => loadSigns()).catch(() => {});
  };

  const activeCount = signs.filter((s) => s.active).length;

  return (
    <div className="dashboard-fullmap">
      {/* Full-screen map */}
      <MapContainer center={[-4.325, 15.322]} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <ClickHandler onClick={handleMapClick} />
        {signs.map((s) => {
          const st = s.sign_type ? signTypeMap[s.sign_type] : null;
          return (
            <React.Fragment key={s.id}>
              <Marker position={[s.lat, s.lng]}>
                <Popup>
                  <strong>{st?.label || s.sign_type_label || s.kind}</strong> {s.value && `(${s.value} km/h)`}<br />
                  {s.label}<br />
                  Rayon: {s.radius_m}m &bull; {s.active ? '✅ Actif' : '❌ Inactif'}
                </Popup>
              </Marker>
              {s.active && (
                <Circle center={[s.lat, s.lng]} radius={s.radius_m}
                  pathOptions={{ color: '#ff9100', fillColor: '#ff9100', fillOpacity: 0.08, weight: 1 }} />
              )}
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Floating stats bar — top */}
      <div className="floating-stats">
        <div className="stat-card" style={{ minWidth: 100 }}>
          <div className="value" style={{ color: '#ff9100' }}>{signs.length}</div>
          <div className="label">Total</div>
        </div>
        <div className="stat-card" style={{ minWidth: 100 }}>
          <div className="value" style={{ color: '#00e676' }}>{activeCount}</div>
          <div className="label">Actifs</div>
        </div>
        <div className="stat-card" style={{ minWidth: 100 }}>
          <div className="value" style={{ color: '#2979ff' }}>{signTypes.length}</div>
          <div className="label">Types</div>
        </div>
      </div>

      {/* Floating toolbar — bottom center */}
      <div className="map-toolbar">
        <button className={`map-toggle-btn${showList ? ' active' : ''}`} onClick={() => setShowList(!showList)}>
          <i className="bi bi-list-ul"></i>
          <span className="fab-label">Panneaux</span>
        </button>
        <button className={`map-toggle-btn${showForm ? ' active' : ''}`} onClick={() => setShowForm(!showForm)}>
          <i className="bi bi-plus-circle"></i>
          <span className="fab-label">Ajouter</span>
        </button>
      </div>

      {/* Floating sign list — right panel */}
      {showList && (
        <div className="floating-panel">
          <div className="fp-section">
            <h6 className="fp-title" style={{ color: '#ff9100' }}>
              <i className="bi bi-signpost-split"></i> Panneaux virtuels
              <span className="fp-count" style={{ background: 'rgba(255,145,0,0.15)', color: '#ff9100' }}>{signs.length}</span>
            </h6>
            <div style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
              {signs.length === 0 && (
                <p style={{ color: '#888', fontSize: 12, textAlign: 'center', padding: 20 }}>
                  Aucun panneau. Cliquez sur la carte pour en ajouter.
                </p>
              )}
              {signs.map((s) => {
                const st = s.sign_type ? signTypeMap[s.sign_type] : null;
                return (
                  <div key={s.id} className="sign-list-item">
                    {st?.image ? (
                      <img src={st.image} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                    ) : s.sign_type_image ? (
                      <img src={s.sign_type_image} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div className="sign-icon" style={{ color: s.active ? '#ff9100' : '#555' }}>
                        <i className="bi bi-signpost-split"></i>
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: s.active ? '#fff' : '#666' }}>
                        {st?.label || s.sign_type_label || s.kind}
                      </div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {s.value ? `${s.value} km/h · ` : ''}{s.radius_m}m
                      </div>
                    </div>
                    <Form.Check type="switch" checked={s.active} onChange={() => handleToggle(s)}
                      style={{ marginRight: 4 }} />
                    <button className="locate-btn" onClick={() => handleDelete(s.id)}
                      style={{ color: '#ff1744' }}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Floating add-sign form — bottom left */}
      {showForm && (
        <div className="signs-float-form">
          <div className="fp-section">
            <h6 className="fp-title">
              <i className="bi bi-plus-circle" style={{ color: '#00e676' }}></i> Nouveau panneau
              <button onClick={() => setShowForm(false)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 16 }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </h6>
            <Form.Group className="mb-2">
              <Form.Label className="text-muted" style={{ fontSize: 11 }}>Type de signalisation</Form.Label>
              <Form.Select size="sm" value={form.sign_type || ''} onChange={(e) => handleSignTypeSelect(e.target.value)}
                style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 12 }}>
                <option value="">— Choisir un type —</option>
                {signTypes.filter((t) => t.active).map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {form.sign_type && signTypeMap[form.sign_type]?.image && (
              <div className="text-center mb-2">
                <img src={signTypeMap[form.sign_type].image} alt=""
                  style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '2px solid #ff9100' }} />
              </div>
            )}
            <div className="d-flex gap-2">
              <Form.Group className="mb-2" style={{ flex: 1 }}>
                <Form.Label className="text-muted" style={{ fontSize: 11 }}>Valeur (km/h)</Form.Label>
                <Form.Control size="sm" type="number" value={form.value || ''}
                  onChange={(e) => setForm({ ...form, value: parseInt(e.target.value) || null })}
                  style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 12 }} />
              </Form.Group>
              <Form.Group className="mb-2" style={{ flex: 1 }}>
                <Form.Label className="text-muted" style={{ fontSize: 11 }}>Rayon (m)</Form.Label>
                <Form.Control size="sm" type="number" value={form.radius_m}
                  onChange={(e) => setForm({ ...form, radius_m: parseInt(e.target.value) || 500 })}
                  style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 12 }} />
              </Form.Group>
            </div>
            <Form.Group className="mb-2">
              <Form.Label className="text-muted" style={{ fontSize: 11 }}>Message vocal</Form.Label>
              <Form.Control size="sm" value={form.message_fr}
                onChange={(e) => setForm({ ...form, message_fr: e.target.value })}
                placeholder="Attention, limitation..."
                style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 12 }} />
            </Form.Group>
            <div className="d-flex align-items-center justify-content-between">
              <span style={{ fontSize: 11, color: '#888' }}>
                <i className="bi bi-geo-alt"></i> {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
              </span>
              <button className="btn btn-sm" onClick={handleSave}
                disabled={!form.sign_type}
                style={{ background: form.sign_type ? '#00e676' : '#555', color: '#000', fontWeight: 600, fontSize: 12, borderRadius: 20, padding: '4px 16px' }}>
                <i className="bi bi-check-lg"></i> Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
